import { ApplicationType } from '@grouper/modules/core/applicationTypes'
import BuilderForm from '@grouper/modules/builder/components/form/BuilderForm'
import SidebarComponentBuilder from '@grouper/modules/builder/components/sidebar/SidebarComponentBuilder'
import { populatePage } from '@grouper/modules/builder/store/page'
import BuilderApplicationContext from '@grouper/modules/builder/components/application/BuilderApplicationContext'
import { DataProviderType } from '@grouper/modules/core/dataProviderTypes'
import { DEVELOPMENT_STAGES } from '@grouper/modules/core/constants'
import { pageFinished } from '@grouper/modules/core/utils/routing'
import { nextTick } from '#imports'

export class BuilderApplicationType extends ApplicationType {
  static getType() {
    return 'builder'
  }

  getIconClass() {
    return 'grouper-icon-application'
  }

  getName() {
    const { $i18n: i18n } = this.app
    return i18n.t('applicationType.builder')
  }

  getNamePlural() {
    const { $i18n: i18n } = this.app
    return i18n.t('applicationType.builders')
  }

  getDescription() {
    const { $i18n: i18n } = this.app
    return i18n.t('applicationType.builderDesc')
  }

  getDefaultName() {
    const { $i18n: i18n } = this.app
    return i18n.t('applicationType.builderDefaultName')
  }

  supportsTrash() {
    return false
  }

  getApplicationFormComponent() {
    return BuilderForm
  }

  getSidebarComponent() {
    return SidebarComponentBuilder
  }

  getApplicationContextComponent() {
    return BuilderApplicationContext
  }




  populate(application) {
    const values = super.populate(application)
    values.pages = values.pages.map(populatePage)
    if (!values.integrations) {
      values.integrations = []
    }
    if (!values.user_sources) {
      values.user_sources = []
    }
    if (!values.selectedElement) {
      values.selectedElement = null
    }
    values._loadedOnce = false

    values.userSourceUser = null
    return values
  }

  getFrontendUrls(application) {
    const domains = this.app.$store.getters['domain/getDomains']

    return [
      ...domains.map((domain) => {
        const url = new URL(this.app.$config.public.publicWebFrontendUrl)
        return `${url.protocol}//${domain.domain_name}${
          url.port ? `:${url.port}` : ''
        }`
      }),
    ]
  }

  delete(application) {
    const { $store, $router } = this.app
    const pageSelected = $store.getters['page/getVisiblePages'](
      application
    ).some((page) => page._.selected)

    if (pageSelected) {
      $router.push({ name: 'dashboard' })
    }
  }

  async loadExtraData(builder, mode) {
    const { $store, $registry } = this.app
    if (!builder._loadedOnce) {
      const sharedPage = $store.getters['page/getSharedPage'](builder)
      await Promise.all([
        $store.dispatch('userSource/fetch', {
          application: builder,
        }),
        $store.dispatch('integration/fetch', {
          application: builder,
        }),
        // Fetch shared data sources
        $store.dispatch('dataSource/fetch', {
          page: sharedPage,
        }),
        $store.dispatch('element/fetch', {
          builder,
          page: sharedPage,
        }),
        $store.dispatch('builderWorkflowAction/fetch', { page: sharedPage }),
      ])

      // Initialize application shared stuff like data sources
      await DataProviderType.initOnceAll(
        $registry.getAll('builderDataProvider'),
        {
          builder,
          mode,
        }
      )

      await $store.dispatch('application/forceUpdate', {
        application: builder,
        data: { _loadedOnce: true },
      })
    }
  }

  async select(application) {
    const { $router, $store, $i18n } = this.app

    const pages = $store.getters['page/getVisiblePages'](application)

    if (pages.length > 0) {
      await $router.push({
        name: 'builder-page',
        params: {
          builderId: application.id,
          pageId: pages[0].id,
        },
      })
      await pageFinished(this.app)
      await nextTick()
      return true
    } else {
      $store.dispatch('toast/error', {
        title: $i18n.t('applicationType.cantSelectPageTitle'),
        message: $i18n.t('applicationType.cantSelectPageDescription'),
      })
      return false
    }
  }

  prepareForStoreUpdate(application, data) {
    if (Object.prototype.hasOwnProperty.call(data, 'pages')) {
      delete data.pages
    }

    if (Object.prototype.hasOwnProperty.call(data, 'theme')) {
      delete data.theme
    }

    return data
  }

  get developmentStage() {
    return DEVELOPMENT_STAGES.BETA
  }

  isVisible(application) {
    // We don't want to show a builder application the user doesn't
    // have the permission to list pages.
    return this.app.$hasPermission(
      'builder.list_pages',
      application,
      application.workspace.id
    )
  }

  getOrder() {
    return 70
  }
}
